import { IdCardIcon } from "lucide-react"
import { FullInfluencerFragment, PlatformEnum } from "~/__generated__/graphql"
import { SocialMediaIcon } from "~/common/SocialMediaIcons"
import {
  categoryName,
  collaborationTypeNames,
  creatorTypeName,
  exclusivityName,
} from "~/common/enums"
import { CopyButton } from "~/ui/CopyButton"
import { IconHeading } from "~/ui/IconHeading"
import { Tag } from "~/ui/Tag"

const ClickableSocialMediaIcon = ({
  url,
  platform,
}: {
  url: string
  platform: PlatformEnum
}) => {
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <SocialMediaIcon platform={platform} />
    </a>
  )
}

const countryTagName = (influencer: FullInfluencerFragment) => {
  if (influencer.countryData) {
    return `${influencer.countryData.emojiFlag} ${influencer.countryData.name}`
  }
  return influencer.countryCode || ""
}

export const DetailsTab = ({
  influencer,
}: {
  influencer: FullInfluencerFragment
}) => {
  return (
    <div>
      <IconHeading
        icon={<IdCardIcon />}
        title="Influencer Info"
        className="mb-4 mt-2"
      />
      <div className="flex-grow flex flex-col gap-2">
        <div className="font-header font-bold text-xl">{influencer.name}</div>
        <div className="flex flex-row gap-2 items-center">
          {influencer.tikTokProfile?.url && (
            <ClickableSocialMediaIcon
              url={influencer.tikTokProfile.url}
              platform={PlatformEnum.TikTok}
            />
          )}
          {influencer.instagramProfile?.url && (
            <ClickableSocialMediaIcon
              url={influencer.instagramProfile.url}
              platform={PlatformEnum.Instagram}
            />
          )}
          {influencer.youtubeProfile?.url && (
            <ClickableSocialMediaIcon
              url={influencer.youtubeProfile.url}
              platform={PlatformEnum.Youtube}
            />
          )}
        </div>
        <div className="text-defaultSecondary">
          {collaborationTypeNames(influencer.collaborationTypes).join(" • ")}
        </div>
        <div className="flex flex-wrap gap-2">
          {influencer.countryCode && (
            <Tag variant="country" text={countryTagName(influencer)} />
          )}
          {influencer.creatorTypes?.map((creatorType) => (
            <Tag
              key={creatorType}
              variant="creatorType"
              text={creatorTypeName(creatorType)}
            />
          ))}
          {influencer.category && (
            <Tag variant="category" text={categoryName(influencer.category)} />
          )}
        </div>
      </div>

      <IconHeading
        icon={<IdCardIcon />}
        title="Agency Info"
        className="my-4 mt-8"
      />
      <div className="flex-grow flex flex-col gap-2">
        <div className="font-header font-bold text-xl">
          {influencer.agency?.name}
        </div>
        <div className="text-defaultSecondary">
          {exclusivityName(influencer.exclusivity)}
        </div>
        <div className="text-defaultSecondary">{influencer.agentName}</div>
        {influencer.agentPhone && (
          <div className="text-defaultSecondary">{influencer.agentPhone}</div>
        )}
        {influencer.agentEmail && (
          <CopyButton
            label={influencer.agentEmail}
            textToCopy={influencer.agentEmail}
            successMessage={`"${influencer.agentEmail}" copied to clipboard`}
            className="justify-start"
          />
        )}
      </div>
    </div>
  )
}
